import React, { useState } from 'react';
import { Button, Grid, IconButton, InputAdornment, Stack, Typography } from '@mui/material';
import logo from '../../../assets/logo/marmin_logo.png';
import CustomTextInput from '../../../components/CustomTextInput';
import { Link, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { authApiClient } from '../../../utils/axios';
import { showSnackbar } from '../../../api/snackbarSlice';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const UpdatePasswordValidationSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email format').required('Email is required'),
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
    .matches(/\d/, 'Password must contain at least one number')
    .matches(/[@$!%*?&]/, 'Password must contain at least one special character (@, $, !, %, *, ?, &)')
    .required('Password is required'),
  confirmPassword: Yup.string().oneOf([Yup.ref('password'), null], 'Passwords must match'),
});

const ForgotPasswordUpdateForm = ({ email }) => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    trigger,
    setError,
  } = useForm({
    resolver: yupResolver(UpdatePasswordValidationSchema),
    defaultValues: {
      email: email,
      termsAndCondition: false,
    },
  });

  const onSubmit = async (data) => {
    setLoading(true);
    authApiClient
      .patch('/reset-password', data)
      .then((res) => {
        navigate('/login', { replace: true });
        dispatch(
          showSnackbar({
            message: 'Password updated successfully!.',
            severity: 'success',
          }),
        );
      })
      .catch((err) => {
        if (err?.response?.data?.errors?.password) {
          setError('password', { message: err?.response?.data?.errors?.password });
        } else {
          let message =
            Object.values(err?.response?.data?.errors ?? [])
              .flat()
              .join('\n') ||
            err.message ||
            'Something went wrong';
          dispatch(showSnackbar({ message, severity: 'error' }));
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const formData = watch();
  return (
    <>
      <Stack sx={{ alignItems: 'center', mb: 4 }}>
        <img src={logo} alt="logo" width="50%" />
      </Stack>

      <Stack spacing={2}>
        <CustomTextInput name="email" label="Email" control={control} errors={errors} disabled={true} />
        <CustomTextInput
          name="password"
          type={showPassword ? 'text' : 'password'}
          label="Password"
          control={control}
          errors={errors}
          endAdornment={
            <InputAdornment position="end">
              <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          }
        />
        <CustomTextInput
          name="confirmPassword"
          type={showConfirmPassword ? 'text' : 'password'}
          label="Confirm Password"
          control={control}
          errors={errors}
          endAdornment={
            <InputAdornment position="end">
              <IconButton onClick={() => setShowConfirmPassword((prev) => !prev)} edge="end">
                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          }
        />
        <Grid container spacing={0}>
          <Grid size={6}>
            <Typography variant="caption" sx={{ color: formData.password?.length >= 8 ? 'green' : '#ff0000' }}>
              Minimum 8 characters.
            </Typography>
          </Grid>
          <Grid size={6}>
            <Typography variant="caption" sx={{ color: /[A-Z]/.test(formData.password) ? 'green' : '#ff0000' }}>
              At least 1 uppercase letter.
            </Typography>
          </Grid>

          <Grid size={6}>
            <Typography variant="caption" sx={{ color: /[0-9]/.test(formData.password) ? 'green' : '#ff0000' }}>
              At least 1 number.
            </Typography>
          </Grid>
          <Grid size={6}>
            <Typography
              variant="caption"
              sx={{ color: /[!@#$%^&*(),.?":{}|<>]/.test(formData.password) ? 'green' : '#ff0000' }}
            >
              At least one special character.
            </Typography>
          </Grid>
        </Grid>
        <Button type="button" variant="contained" className="submit" onClick={handleSubmit(onSubmit)}>
          {loading ? 'Updating...' : 'Update Password'}
        </Button>
        <Typography variant="body2">
          <Link to="/login" style={{ textDecoration: 'none' }}>
            Back to Login
          </Link>
        </Typography>
      </Stack>
    </>
  );
};

export default ForgotPasswordUpdateForm;
